import React, { memo, useMemo } from 'react'
import { Skeleton } from 'transfix-ui/core'
import Typography from 'transfix-ui/core/Typography'
import { ITcmEquipmentKeyTypes, Maybe } from 'types/graphqlTypes'
import formatCurrency, { formatNumber } from 'utils/formatCurrency'
import { TLaneSchema } from '../../schema/LaneSchema'
import { IEquipmentTypes } from '../../types'
import { parseLocation } from '../../utils'
import { useMarketRateQuery } from './graphql/MarketRate'
import { useTripDistanceQuery } from './graphql/TripDistance'
import { useAverageMarketRateMessageStyles } from './useAverageMarketRateMessageStyles'
import { getTcmEquipment } from './utils'

export interface IAverageMarketRateMessageProps {
  lane: Maybe<TLaneSchema>
  equipment: Maybe<IEquipmentTypes>
}

const AverageMarketRateMessage = memo<IAverageMarketRateMessageProps>(({ equipment, lane }) => {
  const classes = useAverageMarketRateMessageStyles()

  const { dropoff, pickup } = lane || {}
  const { text: pickupLocation } = pickup || {}
  const { text: dropoffLocation } = dropoff || {}
  const { city: pickupCity = '', stateCode: pickupStateCode = '' } =
    parseLocation(pickupLocation) || {}
  const { city: deliveryCity = '', stateCode: deliveryStateCode = '' } =
    parseLocation(dropoffLocation) || {}

  const equipmentKey = getTcmEquipment(equipment)
  const skipQueries =
    !pickupCity || !pickupStateCode || !deliveryCity || !deliveryStateCode || !equipmentKey

  /**
   * Market Rate
   * Skipped if any variable is null
   */
  const { data: marketRateData, loading: loadingMarketRate } = useMarketRateQuery({
    variables: {
      pickupCity,
      pickupStateCode,
      deliveryCity,
      deliveryStateCode,
      equipmentKey: equipmentKey || ITcmEquipmentKeyTypes.dry_van,
    },
    skip: skipQueries,
  })
  const { marketRate = 0 } = marketRateData || {}

  /**
   * Trip Distance
   */
  const { data: tripDistanceData, loading: loadingTripDistance } = useTripDistanceQuery({
    variables: {
      pickupLocation: { city: pickupCity, stateCode: pickupStateCode },
      deliveryLocation: { city: deliveryCity, stateCode: deliveryStateCode },
    },
    skip: skipQueries,
  })
  const { getCalculatedMiles: tripDistance = 0 } = tripDistanceData || {}

  /**
   * Per Mile Rate
   */
  const perMileRate = useMemo(() => {
    if (typeof marketRate === 'number' && typeof tripDistance === 'number') {
      return marketRate / tripDistance
    }
    return 0
  }, [marketRate, tripDistance])

  return (
    <div>
      {loadingMarketRate && (
        <div className={classes.skeletonWrapper}>
          <Skeleton variant='rectangular' height='51px' width='100%' />
        </div>
      )}
      {!loadingMarketRate && !!marketRate && (
        <div className={classes.container} data-test='saved-lane-average-market-rate'>
          <div className={classes.marketRateContainer}>
            <Typography variant='body2' className={classes.text}>
              <strong>Average market rate:</strong>
            </Typography>
            <div className={classes.rateWrapper}>
              <Typography variant='body2' className={classes.text}>
                <strong>{formatCurrency(marketRate)}</strong>
                {perMileRate !== 0 && <span>&nbsp;({formatCurrency(perMileRate)} per mile)</span>}
              </Typography>
            </div>
          </div>
          {!loadingTripDistance && !!tripDistance && (
            <Typography variant='body2' className={classes.text}>
              Trip Distance: {formatNumber(tripDistance)} mi
            </Typography>
          )}
        </div>
      )}
    </div>
  )
})

export default AverageMarketRateMessage
